import ArrowRight from '@crehana/compass.icons.outline/ArrowRight';
import {
  BaseCell,
  BaseHeader,
  TableHeaderFilter,
} from '@crehana/compass.ui/table';
import {
  type ColumnFiltersState,
  createColumnHelper,
} from '@crehana/compass.ui/table/tanstack';
import { toast } from '@crehana/compass.ui/toaster';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@crehana/compass.ui/tooltip';
import { format } from 'date-fns';
import { useMemo } from 'react';
import { AtsCareerSiteVisibilityEnum } from '@/generated/graphql/types';
import { useNavigate } from '@/shared/modern-router';
import { extractIdFromUrl } from '@/shared/utils/extract-id-from-url';
import { useAddResourceBundle } from '../../i18n/use-add-resource-bundle';
import { useTranslationCareerSiteAdminShared } from '../../i18n/use-translations';
import { getTimePassed } from '../../utils';
import type {
  TTableDepartmentsList,
  TTableVacanciesList,
} from './shared/types';

// Helper to create columns for the table.
const columnHelper = createColumnHelper<TTableVacanciesList>();

export const useColumnsListVacancy = (
  departmentsData?: TTableDepartmentsList[],
  columnFilters?: ColumnFiltersState,
) => {
  useAddResourceBundle();
  const { t } = useTranslationCareerSiteAdminShared();
  const navigate = useNavigate();

  // Memoized columns configuration to optimize performance.
  const columns = useMemo(() => {
    return [
      // Column vacancy information.
      columnHelper.accessor(row => row.vacancy, {
        id: 'vacancy',
        header: () => (
          <BaseHeader className="tw-pl-1.6">{t('VACANCY')}</BaseHeader>
        ),
        cell: info => {
          const { title, open_date } = info.getValue();

          return (
            <BaseCell>
              <div className="tw-w-full tw-flex tw-flex-col tw-gap-0.4">
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger className="tw-w-fit">
                      <p className="tw-font-subtitle5 tw-w-fit tw-text-start tw-break-all tw-line-clamp-1">
                        {title}
                      </p>
                    </TooltipTrigger>
                    <TooltipContent>
                      <span className="tw-font-body5">{title}</span>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
                <p className="tw-font-body5 tw-text-neutral-light-700">
                  {t('PUBLISHED_DATE', {
                    interpolation: { days: getTimePassed(open_date) },
                  })}
                </p>
              </div>
            </BaseCell>
          );
        },
      }),
      columnHelper.accessor(row => row.area, {
        id: 'area',
        header: ({ table }) => (
          <BaseHeader className="tw-flex tw-items-center tw-justify-start tw-pl-1.6">
            {t('DEPARTMENT')}
            {!!departmentsData?.length && (
              <TableHeaderFilter
                id="area"
                onFilterChange={table.setColumnFilters}
                columnFilters={columnFilters || []}
                options={
                  departmentsData?.map(dept => ({
                    value: dept.id,
                    label: dept.name,
                  })) || []
                }
              />
            )}
          </BaseHeader>
        ),
        cell: info => {
          const area = info.getValue();

          return (
            <BaseCell>
              <p className="tw-font-body5 tw-text-neutral-light-700 tw-break-all tw-line-clamp-1">
                {area.name}
              </p>
            </BaseCell>
          );
        },
      }),
      columnHelper.accessor(row => row.hiring_number, {
        id: 'hiring_number',
        header: () => (
          <BaseHeader className="tw-pl-1">{t('POSITION')}</BaseHeader>
        ),
        cell: info => {
          const hiring_number = info.getValue();

          return (
            <BaseCell>
              <p className="tw-font-body5 tw-text-neutral-light-700 tw-break-all tw-line-clamp-1">
                {hiring_number || 0}
              </p>
            </BaseCell>
          );
        },
      }),
      columnHelper.accessor(row => row.closed_at, {
        id: 'closed_at',
        header: () => (
          <BaseHeader className="tw-pl-1 tw-w-12">
            {t('CLOSED_DATE')}
          </BaseHeader>
        ),
        cell: info => {
          const closed_at = info.getValue();

          return (
            <BaseCell className="tw-w-12">
              <p className="tw-font-body5 tw-text-neutral-light-700">
                {closed_at ? format(new Date(closed_at), 'dd/MM/yyyy') : '-'}
              </p>
            </BaseCell>
          );
        },
      }),
      columnHelper.accessor(row => row.actions, {
        id: 'actions',
        header: () => <BaseHeader className="tw-w-2">{''}</BaseHeader>,
        cell: info => {
          const { vacancy_link, disabled, color, type, career_site_id } =
            info.getValue();
          return (
            <BaseCell className="tw-w-2">
              <button
                type="button"
                disabled={disabled}
                onClick={() => {
                  if (type === AtsCareerSiteVisibilityEnum.InternalAccess) {
                    // Extract vacancy UID from vacancy_link using robust URL parsing
                    const jobId = extractIdFromUrl(vacancy_link);
                    if (jobId) {
                      navigate.navigateTo({
                        path: '/[slug]/ats/career-site/jobs/[jobId]/',
                        params: { jobId },
                        searchParams: career_site_id
                          ? { career_site: career_site_id }
                          : undefined,
                      });
                    } else {
                      toast.error(
                        t('FAILED_TO_EXTRACT_JOB_ID_FROM_VACANCY_LINK'),
                      );
                    }
                  } else {
                    if (!vacancy_link) {
                      toast.error(t('VACANCY_LINK_IS_EMPTY'));
                      return;
                    }
                    // Add career_site_id as query param to the URL if available
                    const url = new URL(vacancy_link, window.location.origin);
                    if (career_site_id) {
                      url.searchParams.set('career_site', career_site_id);
                    }
                    window.open(url.toString(), '_blank');
                  }
                }}
                className="tw-w-full tw-flex tw-items-end tw-gap-0.4 "
                style={{
                  color: color,
                }}
              >
                <ArrowRight className="tw-text-current" size={24} />
              </button>
            </BaseCell>
          );
        },
      }),
    ];
  }, [t, departmentsData, columnFilters, navigate]); // Dependencies for memoization.

  return { columns: columns }; // Returns the configured columns and selected user action.
};
